import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { sanitizeSearchInput } from '@/lib/utils';
import type { Notice, NoticeFormData, NoticeType, NoticePriority, NoticeStatus } from '@/types/notices';
import { isNoticeActive, sortNotices, localInputToISO } from '@/types/notices';

// ============================================================
// Notice Service — Supabase CRUD for notices
// Follows the same pattern as adminService.ts
// ============================================================

// Loosely-typed handle for the notices tables (not in the generated Database
// types). Identical runtime object; relaxes only compile-time table typing.
const db = supabase as unknown as SupabaseClient;

const NOTICE_SELECT = `
  *,
  notice_routes ( route_id, routes:route_id ( id, origin, destination ) ),
  notice_counters ( terminal_id, terminals:terminal_id ( id, name, district ) )
`;

// Shape of a notices row with its joined targeting rows, before flattening.
type RawNotice = Record<string, unknown> & {
  notice_routes?: { routes?: unknown }[] | null;
  notice_counters?: { terminals?: unknown }[] | null;
};

// ─── Helpers to flatten joined data ──────────────────────────
function flattenNotice(raw: RawNotice): Notice {
  const routes = (raw.notice_routes || []).map((nr) => nr.routes).filter(Boolean);
  const counters = (raw.notice_counters || []).map((nc) => nc.terminals).filter(Boolean);
  const { notice_routes, notice_counters, ...rest } = raw;
  return { ...rest, routes, counters } as unknown as Notice;
}

// ────────────────────────────────────────────────────────────
// PUBLIC queries
// ────────────────────────────────────────────────────────────

/** Fetch all currently active published notices */
export async function getActiveNotices(): Promise<Notice[]> {
  const { data } = await db
    .from('notices')
    .select(NOTICE_SELECT)
    .eq('status', 'published')
    .lte('starts_at', new Date().toISOString())
    .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
    .order('starts_at', { ascending: false });

  if (!data) return [];

  const notices = (data as RawNotice[]).map(flattenNotice).filter(isNoticeActive);
  return sortNotices(notices);
}

/** Fetch notices flagged for the top announcement bar */
export async function getBarNotices(): Promise<Notice[]> {
  const { data } = await db
    .from('notices')
    .select(NOTICE_SELECT)
    .eq('status', 'published')
    .eq('show_in_top_bar', true)
    .lte('starts_at', new Date().toISOString())
    .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
    .order('starts_at', { ascending: false });

  if (!data) return [];
  return sortNotices((data as RawNotice[]).map(flattenNotice).filter(isNoticeActive));
}

/** Fetch notices for the homepage section (max N) */
export async function getHomepageNotices(limit = 4): Promise<Notice[]> {
  const { data } = await db
    .from('notices')
    .select(NOTICE_SELECT)
    .eq('status', 'published')
    .eq('show_on_homepage', true)
    .lte('starts_at', new Date().toISOString())
    .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
    .order('is_pinned', { ascending: false })
    .order('starts_at', { ascending: false })
    .limit(limit + 4); // fetch extra to account for expired ones

  if (!data) return [];
  return sortNotices((data as RawNotice[]).map(flattenNotice).filter(isNoticeActive)).slice(0, limit);
}

/** Fetch a single notice by ID */
export async function getNoticeById(id: string): Promise<Notice | null> {
  const { data } = await db
    .from('notices')
    .select(NOTICE_SELECT)
    .eq('id', id)
    .single();

  if (!data) return null;
  return flattenNotice(data as RawNotice);
}

/** Fetch filtered list of published notices for the public Notice Center */
export async function getPublicNotices(filters: {
  search?: string;
  type?: string;
  priority?: string;
  showArchived?: boolean;
}): Promise<Notice[]> {
  let query = db
    .from('notices')
    .select(NOTICE_SELECT)
    .eq('status', 'published')
    .lte('starts_at', new Date().toISOString())
    .order('is_pinned', { ascending: false })
    .order('starts_at', { ascending: false });

  if (filters.type && filters.type !== 'all') {
    query = query.eq('type', filters.type as NoticeType);
  }

  if (filters.priority && filters.priority !== 'all') {
    query = query.eq('priority', filters.priority as NoticePriority);
  }

  if (filters.search) {
    const safe = sanitizeSearchInput(filters.search);
    query = query.or(`title.ilike.%${safe}%,summary.ilike.%${safe}%`);
  }

  const { data } = await query;
  if (!data) return [];

  let notices = (data as RawNotice[]).map(flattenNotice);

  if (!filters.showArchived) {
    notices = notices.filter(isNoticeActive);
  }

  return sortNotices(notices);
}

// ────────────────────────────────────────────────────────────
// ADMIN queries
// ────────────────────────────────────────────────────────────

/** Fetch ALL notices for admin management (no status filter) */
export async function adminGetAllNotices(): Promise<Notice[]> {
  const { data } = await db
    .from('notices')
    .select(NOTICE_SELECT)
    .order('created_at', { ascending: false });

  if (!data) return [];
  return (data as RawNotice[]).map(flattenNotice);
}

/** Create a new notice with optional route/counter targeting */
export async function adminCreateNotice(form: NoticeFormData): Promise<Notice | null> {
  const { route_ids, counter_ids, starts_at, expires_at, cta_label, cta_url, ...rest } = form;

  const { data, error } = await db
    .from('notices')
    .insert({
      ...rest,
      cta_label: cta_label || null,
      cta_url: cta_url || null,
      starts_at: starts_at ? localInputToISO(starts_at) : new Date().toISOString(),
      expires_at: expires_at ? localInputToISO(expires_at) : null,
    })
    .select()
    .single();

  if (error || !data) {
    return null;
  }

  const created = data as RawNotice & { id: string };

  // Insert targeting junction rows
  if (route_ids.length > 0) {
    await db.from('notice_routes').insert(
      route_ids.map(route_id => ({ notice_id: created.id, route_id }))
    );
  }
  if (counter_ids.length > 0) {
    await db.from('notice_counters').insert(
      counter_ids.map(terminal_id => ({ notice_id: created.id, terminal_id }))
    );
  }

  return flattenNotice(created);
}

/** Update an existing notice */
export async function adminUpdateNotice(id: string, form: Partial<NoticeFormData>): Promise<boolean> {
  const { route_ids, counter_ids, starts_at, expires_at, cta_label, cta_url, ...rest } = form;

  const updates: Record<string, unknown> = { ...rest };
  if (cta_label !== undefined) updates.cta_label = cta_label || null;
  if (cta_url !== undefined) updates.cta_url = cta_url || null;
  if (starts_at !== undefined) updates.starts_at = starts_at ? localInputToISO(starts_at) : new Date().toISOString();
  if (expires_at !== undefined) updates.expires_at = expires_at ? localInputToISO(expires_at) : null;

  const { error } = await db
    .from('notices')
    .update(updates)
    .eq('id', id);

  if (error) {
    return false;
  }

  // Re-sync targeting if provided (H-4 fix: save old targets for rollback on insert failure)
  if (route_ids !== undefined) {
    const { data: oldRoutes } = await db.from('notice_routes').select('route_id').eq('notice_id', id);
    await db.from('notice_routes').delete().eq('notice_id', id);
    if (route_ids.length > 0) {
      const { error: routeErr } = await db.from('notice_routes').insert(
        route_ids.map(route_id => ({ notice_id: id, route_id }))
      );
      if (routeErr && oldRoutes && oldRoutes.length > 0) {
        await db.from('notice_routes').insert(
          (oldRoutes as { route_id: string }[]).map((r) => ({ notice_id: id, route_id: r.route_id }))
        );
      }
    }
  }
  if (counter_ids !== undefined) {
    const { data: oldCounters } = await db.from('notice_counters').select('terminal_id').eq('notice_id', id);
    await db.from('notice_counters').delete().eq('notice_id', id);
    if (counter_ids.length > 0) {
      const { error: counterErr } = await db.from('notice_counters').insert(
        counter_ids.map(terminal_id => ({ notice_id: id, terminal_id }))
      );
      if (counterErr && oldCounters && oldCounters.length > 0) {
        await db.from('notice_counters').insert(
          (oldCounters as { terminal_id: string }[]).map((c) => ({ notice_id: id, terminal_id: c.terminal_id }))
        );
      }
    }
  }

  return true;
}

/** Delete a notice */
export async function adminDeleteNotice(id: string): Promise<boolean> {
  const { error } = await supabase.from('notices').delete().eq('id', id);
  if (error) {
    return false;
  }
  return true;
}

/** Quick toggle publish/unpublish */
export async function adminTogglePublish(id: string, currentStatus: string): Promise<boolean> {
  const newStatus: NoticeStatus = currentStatus === 'published' ? 'draft' : 'published';
  const { error } = await db.from('notices').update({ status: newStatus }).eq('id', id);
  return !error;
}

/** Quick toggle pin/unpin */
export async function adminTogglePin(id: string, currentlyPinned: boolean): Promise<boolean> {
  const { error } = await db.from('notices').update({ is_pinned: !currentlyPinned }).eq('id', id);
  return !error;
}

/** Archive a notice */
export async function adminArchiveNotice(id: string): Promise<boolean> {
  const { error } = await db.from('notices').update({ status: 'archived' as NoticeStatus }).eq('id', id);
  return !error;
}
